import { useQuery } from '@apollo/client/react';
import { normalizeSlices, SliceContainer, TextAds } from '@scorenco/components';
import { ssrApolloService, Types } from '@scorenco/core';
import {
  NextAkceloConfig,
  NextGooglePublisherTagConfig,
  NextOptiDigitalConfig,
  NextPageWithLayout,
  NextAdArea,
} from '@scorenco/next';
import Error from 'next/error';

import { DebugMode, DebugModeStrapiLink, StrapiPageSeo } from '@/components';
import { WebLayout } from '@/layouts';
import { RouteKey } from '@/routes';

import { GetSportPagesDocument } from './_internal/Sport.query.generated';
import { SportHero } from './_internal/SportHero';

const Sport: NextPageWithLayout<{ preview?: boolean; sportSlug: string }> = ({
  preview,
  sportSlug,
}) => {
  const slug = sportSlug;
  const displaySportName = slug?.replace(/-/g, ' ') || '';

  const sportPageQuery = useQuery(GetSportPagesDocument, {
    context: {
      clientName: 'strapi',
    },
    variables: {
      slug,
      status: preview
        ? Types.PublicationStatus.Draft
        : Types.PublicationStatus.Published,
    },
    skip: !slug,
  });

  const sportPage = sportPageQuery.data?.pagesSports?.[0];

  if (!sportPage && !sportPageQuery.loading) {
    return <Error statusCode={404} />;
  }

  const title = sportPage?.title;
  const seo = sportPage?.seo;
  const images = sportPage?.images;
  const slices = normalizeSlices(sportPage?.body);
  const heroTitle =
    title ||
    seo?.metaTitle ||
    (displaySportName
      ? `Suivez tous les matchs de ${displaySportName}`
      : undefined);

  return (
    <>
      <NextOptiDigitalConfig adUnit="/157593316/od.scorenco.com/sport" />
      <NextAkceloConfig />
      <NextGooglePublisherTagConfig />

      {seo && (
        <StrapiPageSeo
          seo={seo}
          url={{
            routeKey: RouteKey.Sport,
            params: {
              sport: slug,
            },
          }}
        />
      )}

      {heroTitle && (
        <SportHero key={slug} title={heroTitle} sport={slug} images={images} />
      )}
      <div style={{ backgroundColor: 'white', padding: '1rem 0' }}>
        <div style={{ margin: '0 auto', width: '50%' }}>
          <TextAds
            display_type="homepage"
            sport_id={undefined}
            department_id={undefined}
            logged_in={0}
          />
        </div>
      </div>

      <NextAdArea
        akceloSlotId="Scorenco_PlayRVideo"
        akceloAdUnitClass="akcelo-adunit-15786"
        withGooglePublisherTag
      />

      <SliceContainer slices={slices} />

      <DebugMode preview={preview}>
        <DebugModeStrapiLink
          type="pages-sport"
          strapiId={sportPage?.documentId}
        />
      </DebugMode>
    </>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async (options, acc) => {
    options.res.setHeader(
      'Cache-Control',
      `public, s-maxage=${30 * 24 * 60 * 60}, max-age=${15 * 60}`
    );

    const { apolloClient, addApolloState } = ssrApolloService();

    if (options.query['no-ssr'] === undefined) {
      const getSportPagesQuery = await apolloClient.query({
        query: GetSportPagesDocument,
        context: {
          clientName: 'strapi',
        },
        variables: {
          slug: options.params?.sport as string,
          status: options?.preview
            ? Types.PublicationStatus.Draft
            : Types.PublicationStatus.Published,
        },
      });

      if (!getSportPagesQuery.data?.pagesSports?.[0]) {
        return { notFound: true };
      }
    }

    return addApolloState({
      ...acc,
      props: {
        ...acc.props,
        preview: options?.preview ? true : null,
        sportSlug: options.params?.sport as string,
      },
    });
  },
]);

/**
 * --------------------------------------------------------------------------------
 * nextjs-lambda doesn't support ISR yet. We have a read-only issue.
 * The solution is to shared the ISR cache : https://github.com/vercel/next.js/discussions/19589
 * It's still experimental :'(
 * --------------------------------------------------------------------------------
 *
 * export const getStaticProps = WebLayout.buildGetStaticProps({}, [
 *   async (options: GetStaticPropsContext, acc: { props: any }) => {
 *     const { apolloClient, addApolloState } = ssrApolloService();
 *
 *     try {
 *
 *
 *       await apolloClient.query({
 *         query: GetSportPagesDocument,
 *         context: {
 *           clientName: 'strapi',
 *         },
 *         variables: {
 *           slug: options.params?.landingSlug as string,
 *           status: Types.PublicationStatus.Published,
 *         },
 *       });
 *     } catch (err) {
 *       //Do nothing
 *       console.log('err : ', err);
 *     }
 *
 *     return addApolloState({
 *       ...acc,
 *       props: { ...acc.props, preview: options?.preview ? true : null },
 *     });
 *   },
 * ]);
 *
 *
 * export const getStaticPaths = WebLayout.buildGetStaticPaths([
 *   async () => {
 *     const { apolloClient } = ssrApolloService();
 *
 *     const slugQuery = await apolloClient.query({
 *       query: GetAllLangingPageSlugDocument,
 *       context: {
 *         clientName: 'strapi',
 *       },
 *     });
 *
 *     return {
 *       paths:
 *         slugQuery.data?.pagesSports?.data?.map(
 *           (page) => `/landing/${page.slug}`
 *         ) || [],
 *       fallback: false,
 *     };
 *   },
 * ]);
 *
 **/

Sport.getLayout = WebLayout.buildGetLayout({
  strapiMenu: true,
  amplify: { protectedPage: false },
});

export default Sport;
